import '../../Admin.scss';
import '../../../../styles/_table.scss';

import { Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import { ValidSortProp, dataSorter } from 'utilities/dataSorters';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DefaultRoutes } from 'Routes';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import HeaderSearchbar from 'components/Admin/HeaderSearchbar/HeaderSearchbar';
import { ReactComponent as InfoIcon } from 'images/icons/Info.svg';
import { LearningMaterialModel } from 'types/admin/learningMaterials';
import { Link } from 'react-router-dom';
import Paragraph from 'components/TextComps/Paragraph';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import SortOrderButton from 'components/SortOrderButton/SortOrderButton';
import Tooltip from '@trackman/web-shared-components/components/Tooltip/Tooltip';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import dayjs from 'dayjs';
import { getLanguageShortText } from 'utilities/languages';
import { observer } from 'mobx-react-lite';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useStores } from 'index';

type RowsDisplay = {
  id: string;
  title: string;
  kind: string;
  languages: string;
  description: string;
  createdAt: string;
  updatedAt: string;
};

export const LearningMaterialsList = observer(() => {
  useSetPageTitle({ title: 'Learning Materials List', subtitle: '' });
  const lms = useStores().learningMaterialStore;
  const [isModalOn, setIsModalOn] = useState(false);
  const [sortByProperty, setSortByProperty] = useState<ValidSortProp<LearningMaterialModel>>('title');
  const [sortOrder, setSortOrder] = useState<'ASCENDING' | 'DESCENDING'>('ASCENDING');

  useEffect(() => {
    lms.learningMaterialsList.length === 0 && lms.getLearningMaterials();
    setSortByProperty('title');
    setSortOrder('ASCENDING');
  }, [lms]);

  useEffect(() => {
    let filteredList = lms.learningMaterialsList.filter((material) => material.title.toLowerCase().includes(lms.searchValue.toLowerCase()));
    if (sortByProperty) {
      filteredList = dataSorter(filteredList, sortByProperty, sortOrder);
    }

    lms.filteredList = filteredList;
  }, [lms, lms.searchValue, sortByProperty, sortOrder]);

  const handleSortChange = (property: ValidSortProp<LearningMaterialModel>, order: 'ASCENDING' | 'DESCENDING') => {
    setSortByProperty(property);
    setSortOrder(order);
  };

  const rowsDisplay: RowsDisplay[] = useMemo(
    () =>
      lms.filteredList.map((material) => {
        return {
          id: material.id,
          title: material.title,
          languages: material.languages ? material.languages.map((lng) => getLanguageShortText(lng)).join(', ') : '',
          kind: material.kind,
          description: material.descriptions.find((description) => description.countryCode === 'en-US')?.text,
          createdAt: dayjs(material.createdAt).format('MMM DD, YYYY'),
          updatedAt: dayjs(material.updatedAt).format('MMM DD, YYYY'),
        };
      }) as RowsDisplay[],
    [lms.filteredList]
  );

  const deleteLearningMaterial = useCallback(() => {
    lms.deleteLearningMaterialAndUpdate();
    setIsModalOn(false);
  }, [lms, setIsModalOn]);

  return (
    <div className='admin-content-container'>
      <div className='admin-list-header'>
        <HeaderSearchbar searchValue={lms.searchValue} onChange={lms.setSearchValue} />
        <HeaderCreateButton buttonTitle='New learning material' route={DefaultRoutes.LearningMaterialsEditor} />
      </div>

      <div className='tmu-table-wrapper'>
        <Table className='tmu-table admin-table'>
          <TableHead>
            <TableRow>
              <TableCell>
                Name
                <SortOrderButton
                  isActive={sortByProperty === 'title'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('title', sortOrder)}
                />
              </TableCell>
              <TableCell>Languages</TableCell>
              <TableCell>
                Kind
                <SortOrderButton
                  isActive={sortByProperty === 'kind'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('kind', sortOrder)}
                />
              </TableCell>
              <TableCell>
                Date created
                <SortOrderButton
                  isActive={sortByProperty === 'createdAt'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('createdAt', sortOrder)}
                />
              </TableCell>
              <TableCell>
                Last edited
                <SortOrderButton
                  isActive={sortByProperty === 'updatedAt'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('updatedAt', sortOrder)}
                />
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsDisplay.length ? (
              rowsDisplay.map((row) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    <TableCell>
                      <div className='description-tooltip'>
                        {row.title.toLowerCase()}
                        <Tooltip
                          title={row.description ? row.description : 'No description'}
                          placement='bottom'
                          className='table-cell-tooltip'
                        >
                          <InfoIcon />
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell className='languages'>{row.languages}</TableCell>
                    <TableCell>{row.kind}</TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>{row.updatedAt}</TableCell>
                    <TableCell>
                      <div className='action-buttons'>
                        <Link to={`/Admin/${DefaultRoutes.LearningMaterialsEditor}/${row.id}`} className='row-button'>
                          <PencilIcon />
                        </Link>
                        <button
                          className={`row-button ${lms.isLoading ? 'disabled' : ''}`}
                          onClick={() => {
                            lms.selectedMaterialId = row.id;
                            setIsModalOn(true);
                          }}
                          disabled={lms.isLoading}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <Paragraph>
                    <>No learning materials found</>
                  </Paragraph>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DeleteListItemModal
        isModalOn={isModalOn}
        setIsModalOn={setIsModalOn}
        deleteAction={deleteLearningMaterial}
        itemName='learning material'
      />
    </div>
  );
});

export default LearningMaterialsList;
